// ============================================================================
// KaratScan — live API layer
// ----------------------------------------------------------------------------
// This is the seam between the UI and a REAL backend. The components in this
// app render an *internal* item shape (camelCase, numbers). The public API
// speaks snake_case and serializes money/ratio/weight as STRINGS. Everything
// here: (1) calls the real endpoints, (2) normalizes responses into the exact
// shape the components already consume.
//
// THE SWITCH:  DATA_MODE === "mock" | "live"
//   - mock: app.jsx keeps using the generated SEED_ITEMS + simulated stream.
//   - live: app.jsx fetches public endpoints and receives public SSE updates.
//   Flip it in index.html, or append ?data=mock to use generated local data.
//
// Auth is a cookie (HttpOnly) set by the magic-link consume call, so every
// request just sends credentials:"include" — there is no token to manage.
// ============================================================================

import { API_BASE } from "./config.jsx";
import {
  KARAT_PURITY,
  SPOT_PER_GRAM,
  canonicalNearMissTierCode,
  dealTierThreshold,
  isNearMiss,
  setDealTierEntries,
  tierFor,
} from "./data.jsx";

  const LOCATION_HREF =
    typeof window !== "undefined" && window.location ? window.location.href : "http://localhost/";
  const API_ORIGIN = new URL(API_BASE, LOCATION_HREF).origin;

  // ---- error envelope -------------------------------------------------------
  class ApiError extends Error {
    constructor({ status, code, message, requestId, retryAfter }) {
      super(message || "Request failed");
      this.name = "ApiError";
      this.status = status;
      this.code = code;
      this.requestId = requestId || null;
      this.retryAfter = retryAfter || null;
    }
  }

  async function apiFetch(path, opts = {}) {
    const { method = "GET", body, query, headers, signal, expectedStatuses } = opts;
    let url = API_BASE + path;
    if (query) {
      const qs = new URLSearchParams();
      Object.entries(query).forEach(([k, v]) => {
        if (v == null || v === "") return;
        if (Array.isArray(v)) v.forEach((x) => x != null && x !== "" && qs.append(k, x));
        else qs.append(k, v);
      });
      const s = qs.toString();
      if (s) url += "?" + s;
    }
    const res = await fetch(url, {
      method,
      signal,
      credentials: "include", // carry the session cookie
      headers: {
        Accept: "application/json",
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
    });
    let data = null;
    if (res.status !== 204) {
      try { data = await res.json(); } catch (_) { /* empty/non-json */ }
    }
    const expectedStatus = !expectedStatuses || expectedStatuses.includes(res.status);
    if (!res.ok || !expectedStatus) {
      const env = (data && data.error) || {};
      const error = new ApiError({
        status: res.status,
        code: env.code || (expectedStatus ? String(res.status) : "UNEXPECTED_STATUS"),
        message: env.message || (expectedStatus
          ? res.statusText || "Request failed"
          : `Unexpected response status: ${res.status}`),
        requestId: env.request_id,
        retryAfter: Number(res.headers.get("Retry-After")) || null,
      });
      if (res.status === 401 && isAuthoritativeAccountPath(path)) {
        accountUnauthorizedHandler?.({ path, method: String(method).toUpperCase(), error });
      }
      throw error;
    }
    if (res.status === 204) return null;
    return data;
  }

  // ---- value coercion (strings -> numbers) ---------------------------------
  const num = (x) => (x == null || x === "" ? null : Number(x));
  const int = (x) => { const n = num(x); return n == null ? null : Math.round(n); };
  function valueScoreForRatio(ratio) {
    const n = num(ratio);
    if (n == null || !Number.isFinite(n)) return null;
    const score = Math.round((1.8 - n) / (1.8 - 1.0) * 100);
    return Math.max(0, Math.min(100, score));
  }
  function valueTierForRatio(ratio) {
    const n = num(ratio);
    if (n == null || !Number.isFinite(n)) return null;
    if (n <= 1.00) return "S";
    if (n <= 1.20) return "A";
    if (n <= 1.40) return "B";
    if (n <= 1.60) return "C";
    if (n <= 1.80) return "D";
    return "F";
  }
  function valueCriterionForMinDealScore(rawScore) {
    const score = num(rawScore);
    if (score == null || !Number.isFinite(score) || score <= 0) return "value threshold set";
    const maxRatio = 1 / score;
    const tier = valueTierForRatio(maxRatio);
    const ratioText = `≤${maxRatio.toFixed(2)}x melt`;
    return tier && tier !== "F"
      ? `value tier ${tier} or better (${ratioText})`
      : `price-to-melt ${ratioText}`;
  }
  const confidenceBand = (x, fallback = "medium") => {
    if (x == null || x === "") return fallback;
    const raw = String(x).trim().toLowerCase();
    if (raw === "high" || raw === "medium" || raw === "low") return raw;
    const n = Number(raw);
    if (!Number.isFinite(n)) return fallback;
    if (n >= 0.8) return "high";
    if (n >= 0.5) return "medium";
    return "low";
  };
  const normalizerDeps = (opts = {}) => ({
    purityTable: opts.purityTable || KARAT_PURITY,
    nowMs: Number.isFinite(opts.nowMs) ? opts.nowMs : Date.now(),
    spotPerGram: opts.spotPerGram ?? SPOT_PER_GRAM,
    tierFor: opts.tierFor || tierFor,
    isNearMiss: opts.isNearMiss || isNearMiss,
  });
  function derivedGoldWeightFromPricing(pricing, primaryMetal) {
    const status = pricing && pricing.status;
    const isPriced = status === "priced" || status === "priced_review_required";
    if (!isPriced || String(primaryMetal || "").toLowerCase() !== "gold") return null;
    const meltValue = num(pricing.estimated_gold_melt_value_usd);
    const spotPerGram = num(pricing.gold_spot_price_per_gram_usd);
    if (
      meltValue == null ||
      spotPerGram == null ||
      !Number.isFinite(meltValue) ||
      !Number.isFinite(spotPerGram) ||
      meltValue <= 0 ||
      spotPerGram <= 0
    ) {
      return null;
    }
    return Number((meltValue / spotPerGram).toFixed(3));
  }
  const normalizeImageUrl = (url) => {
    if (!url) return null;
    try {
      const u = new URL(url, LOCATION_HREF);
      if (u.pathname.startsWith("/api/v1/images/")) {
        return API_ORIGIN + u.pathname + u.search + u.hash;
      }
    } catch (_) {
      return null;
    }
    // The public UI should not bypass the API image proxy. If a live row has
    // only a raw marketplace image URL, render the local placeholder instead.
    return null;
  };

  // ---- normalizers: API shape -> internal UI shape -------------------------
  // PublicItemSummary / PublicItemDetail -> the object feed.jsx & detail.jsx render.
  function normalizeItem(a, opts = {}) {
    if (!a) return null;
    const deps = normalizerDeps(opts);
    const p = a.pricing || {};
    const w = a.weight || {};
    const reasonCodes = p.reason_codes || [];
    const ratio = num(p.price_to_melt_ratio);
    const karat = a.karat ?? null;
    const primaryMetal = a.primary_metal || a.metal_family_guess || "unknown";
    const directGoldWeight = num(w.gold_weight_g_estimate);
    const derivedGoldWeight =
      directGoldWeight == null ? derivedGoldWeightFromPricing(p, primaryMetal) : null;

    const isPlated = reasonCodes.includes("gold_plated_not_priced");
    const isFilled = reasonCodes.includes("gold_filled_not_priced");
    const isVermeil = reasonCodes.includes("vermeil_not_priced");
    const isNonGold =
      (a.primary_metal && a.primary_metal !== "gold") ||
      reasonCodes.includes("non_gold_not_priced");
    const isSolidGold =
      typeof a.is_solid_gold === "boolean"
        ? a.is_solid_gold
        : !isPlated && !isFilled && !isVermeil && !isNonGold;

    // images: prefer proxied_url per the brief, and dedupe primary + detail gallery.
    const imgCandidates = [
      a.primary_image,
      ...(Array.isArray(a.images) ? a.images : []),
    ].filter(Boolean);
    const imageUrls = Array.from(new Set(
      imgCandidates
        .map((im) => im && normalizeImageUrl(im.proxied_url || im.url))
        .filter(Boolean)
    ));
    const primaryUrl = imageUrls[0] || null;

    const listingTime = a.listing_time_utc || a.processed_at;
    const ageMin = listingTime
      ? Math.max(0, (deps.nowMs - Date.parse(listingTime)) / 60000)
      : 0;

    const seller = a.seller || {};
    const rawDealScore = num(p.deal_score);
    const valueScore = valueScoreForRatio(ratio);
    const valueTier = valueTierForRatio(ratio);

    return {
      id: String(a.id),
      itemId: a.item_id || "",
      title: a.title || "Untitled listing",
      listingUrl: a.listing_url || "#",
      currency: a.currency || "USD",
      listingTimeUtc: a.listing_time_utc || null,
      processedAt: a.processed_at || null,

      itemType: a.item_type || a.jewelry_type_guess || "other",
      primaryMetal,
      karat,
      purity: karat ? (deps.purityTable[karat] ?? 0) : 0,

      totalWeight: num(w.total_weight_g),
      goldWeight: directGoldWeight ?? derivedGoldWeight,
      goldWeightDerived: directGoldWeight == null && derivedGoldWeight != null,
      weightBasis: p.weight_basis || (w.total_weight_g != null ? "text" : "none"),
      weightSource: w.weight_source || null,
      weightConfidence: confidenceBand(w.weight_confidence),
      weightRequiresReview: !!w.weight_requires_review,

      hasStones: reasonCodes.includes("stones_present"),
      isSolidGold,
      isPlated, isFilled, isVermeil, isNonGold,

      meltValue: num(p.estimated_gold_melt_value_usd),
      ratio,
      valueScore,
      valueTier,
      dealScore: rawDealScore,
      pricingStatus: p.status || null,
      requiresReview: !!p.requires_review,
      reasonCodes,
      spotPerGram: num(p.gold_spot_price_per_gram_usd),

      price: num(a.listing_price_usd ?? a.all_in_price_usd),
      shipping: num(a.shipping_cost_usd),
      shippingStatus: a.shipping_cost_status || (a.shipping_cost_usd != null ? "known" : "unknown"),
      allIn: num(a.all_in_price_usd),

      overallConfidence: confidenceBand(a.overall_confidence),
      coverage: a.coverage || "parsed",
      tier: deps.tierFor(ratio),
      nearMiss: deps.isNearMiss(ratio, opts.nearTier || "steal"),

      seller: seller.username || null,
      location: seller.location || null,
      sellerFeedbackScore: seller.feedback_score ?? null,
      sellerFeedbackPercent: seller.feedback_percent ?? null,

      authenticityGuarantee: a.ebay_authenticity_guarantee === true,
      bestOffer: a.best_offer_enabled === true,
      buyItNow: true,

      images: int(a.image_count) ?? (imageUrls.length || (primaryUrl ? 1 : 0)),
      imageUrl: primaryUrl,
      imageUrls,

      isSavedByMe: a.is_saved_by_me ?? null,
      ageMin,
      condition: a.condition_normalized || a.condition_raw || "good",
      note: null,

      // detail-only extras (present after GET /items/{pk})
      categoryPath: a.category_path || [],
      parseWarnings: a.parse_warnings || [],
      weightEvidence: a.weight_evidence
        ? {
            hasImageScaleAnalysis: !!a.weight_evidence.has_image_scale_analysis,
            listingTextExcerpt: a.weight_evidence.listing_text_excerpt || null,
            imageScaleTextExcerpt: a.weight_evidence.image_scale_text_excerpt || null,
            reviewRequiredReason: a.weight_evidence.review_required_reason || null,
          }
        : null,
      authenticityTermsUrl: a.ebay_authenticity_guarantee_terms_url || null,
      relatedItemsUrl: a.related_items_url || null,
      _raw: a,
    };
  }

  // detail is a superset of summary — same normalizer covers it
  const normalizeDetail = normalizeItem;

  function normalizeSpot(a, opts = {}) {
    const deps = normalizerDeps(opts);
    const perG = num(a.price_per_gram_usd) ?? deps.spotPerGram;
    const previousRaw =
      a.previous_live ||
      (a.metals && a.metals.gold && a.metals.gold.previous_live) ||
      null;
    const previousPerG = previousRaw ? num(previousRaw.price_per_gram_usd) : null;
    const age =
      a.age_seconds != null
        ? a.age_seconds
        : a.fetched_at
        ? Math.max(0, Math.round((deps.nowMs - Date.parse(a.fetched_at)) / 1000))
        : 0;
    return {
      pricePerGram: perG,
      pricePerOz: +(perG * 31.1035).toFixed(2),
      source: a.source || "live",
      isAuthoritative: a.is_authoritative ?? true,
      ageSeconds: age,
      previousLive: previousPerG == null
        ? null
        : {
            pricePerGram: previousPerG,
            fetchedAt: previousRaw.fetched_at || null,
            provider: previousRaw.provider || null,
          },
      change24h: 0, // not in the API; retained for older UI callers
    };
  }

  // PublicSearchItem adds highlights + the response adds total_count
  function normalizeSearch(resp, opts = {}) {
    const items = (resp.items || []).map((it) => ({
      ...normalizeItem(it, opts),
      highlights: it.highlights || null,
    }));
    return { items, totalCount: resp.total_count ?? items.length, nextPage: resp.next_page || null };
  }

  // alert rule -> the shape RightRail's AlertCard renders
  function criteriaToString(c = {}) {
    const parts = [];
    if (c.karats && c.karats.length) parts.push(c.karats.map((k) => k + "K").join(","));
    if (c.item_types && c.item_types.length) parts.push(c.item_types.join("/"));
    if (c.require_solid_gold) parts.push("solid");
    if (c.min_gold_weight_g != null) parts.push("≥" + c.min_gold_weight_g + "g");
    if (c.max_price_to_melt_ratio != null)
      parts.push("≤" + Number(c.max_price_to_melt_ratio).toFixed(2) + "×");
    if (c.min_deal_score != null) parts.push(valueCriterionForMinDealScore(c.min_deal_score));
    if (c.max_all_in_price != null) parts.push("≤$" + c.max_all_in_price);
    return parts.join(" · ") || "any item";
  }
  function normalizeAlertRule(a) {
    const crit = a.criteria || {};
    const pol = a.notification_policy || {};
    const chans = pol.channel_types || pol.channels || [];
    const r = crit.max_price_to_melt_ratio != null ? Number(crit.max_price_to_melt_ratio) : null;
    return {
      id: String(a.id),
      name: a.name || "Untitled rule",
      enabled: a.is_enabled ?? true,
      version: a.version ?? 1,
      criteria: criteriaToString(crit),
      channels: (Array.isArray(chans) ? chans : []).map((c) =>
        typeof c === "string" ? c : c.channel_type || c.type || "discord"
      ),
      lastTriggered: a.last_triggered_at_human || "—",
      matches24h: a.matches_24h ?? 0,
      tier: tierFor(r) || "steal",
      _raw: a,
    };
  }

  function rows(resp) {
    return (resp && (resp.items || resp.data)) || (Array.isArray(resp) ? resp : []);
  }

  const BROWSE_COUNT_KEYS = [
    "todays_deals",
    "deep_discount",
    "steal",
    "deal",
    "negotiable",
    "unscored_gold",
    "all",
  ];

  function browseFilterQuery(filters = {}) {
    const itemType = filters.item_type ?? filters.type;
    return {
      karat: filters.karat,
      item_type: itemType,
    };
  }

  function reviewRequiredDealQuery(includeReview) {
    return { include_review_required: includeReview ? "true" : undefined };
  }

  function reviewRequiredItemQuery(includeReview) {
    return {
      pricing_requires_review: includeReview ? undefined : "false",
      weight_requires_review: includeReview ? undefined : "false",
    };
  }

  function normalizeBrowseCounts(resp = {}) {
    return Object.fromEntries(
      BROWSE_COUNT_KEYS.map((key) => [key, int(resp[key]) ?? 0]),
    );
  }

  let accountUnauthorizedHandler = null;
  const isAuthoritativeAccountPath = (path) =>
    path === "/users/me" || path.startsWith("/me/");
  const setAccountUnauthorizedHandler = (handler) => {
    accountUnauthorizedHandler = typeof handler === "function" ? handler : null;
    return () => {
      if (accountUnauthorizedHandler === handler) accountUnauthorizedHandler = null;
    };
  };

  function normalizeBrowseActivity(resp = {}) {
    return {
      lastAnalyzedAt: resp.last_analyzed_at || null,
    };
  }

  const DEFAULT_TICKER_METRIC_KEYS = [
    "gold_price",
    "best_under_melt",
    "best_great_deal",
    "best_accepts_offers",
    "median_ratio",
    "median_all_in_price",
    "top_karat_deals",
    "top_item_type",
    "avg_process_lag",
    "avg_confidence",
    "largest_melt_spread",
    "top_value_tier",
    "newest_deal",
  ];
  const DEFAULT_TICKER_METRIC_CONFIG = {
    lookbackHours: 24,
    pageLimit: 200,
    maxPages: 8,
    refreshMs: 5 * 60 * 1000,
    sort: "listing_time_desc",
    futureSkewMs: 5 * 60 * 1000,
    trendMinPct: 0.01,
    metricKeys: DEFAULT_TICKER_METRIC_KEYS,
  };
  const TICKER_THRESHOLD_FALLBACKS = {
    deep_discount: 1.0,
    steal: 1.2,
    deal: 1.4,
    negotiable: 1.4,
  };
  const CONFIDENCE_SCORES = { high: 0.9, medium: 0.65, low: 0.35 };
  const ITEM_TYPE_TICKER_LABELS = {
    ring: "Rings",
    necklace: "Necklaces",
    bracelet: "Bracelets",
    earrings: "Earrings",
    pendant: "Pendants",
    chain: "Chains",
    brooch: "Brooches",
    bangle: "Bangles",
    pin: "Pins",
    watch: "Watches",
    charm: "Charms",
    anklet: "Anklets",
    set: "Sets",
    other: "Other",
  };

  const finiteNum = (x) => {
    const n = num(x);
    return Number.isFinite(n) ? n : null;
  };
  const positiveNum = (x, fallback) => {
    const n = Number(x);
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };
  const clampInt = (x, fallback, min, max) => {
    const n = Math.round(positiveNum(x, fallback));
    return Math.max(min, Math.min(max, n));
  };
  const formatLookback = (hours) => {
    const n = Number(hours);
    if (!Number.isFinite(n)) return "24h";
    return Number.isInteger(n) ? `${n}h` : `${n.toFixed(1)}h`;
  };

  function resolveTickerMetricConfig(overrides = {}) {
    const raw = {
      ...DEFAULT_TICKER_METRIC_CONFIG,
      ...(typeof window !== "undefined" && window.KARATSCAN_TICKER_CONFIG ? window.KARATSCAN_TICKER_CONFIG : {}),
      ...(overrides || {}),
    };
    const lookbackHours = positiveNum(raw.lookbackHours, DEFAULT_TICKER_METRIC_CONFIG.lookbackHours);
    const metricKeys = Array.isArray(raw.metricKeys) && raw.metricKeys.length
      ? raw.metricKeys.map((key) => {
          const normalized = String(key);
          return normalized === "highest_deal_score" || normalized === "highest_value_score"
            ? "top_value_tier"
            : normalized;
        })
      : DEFAULT_TICKER_METRIC_KEYS;
    return {
      lookbackHours,
      pageLimit: clampInt(raw.pageLimit, DEFAULT_TICKER_METRIC_CONFIG.pageLimit, 1, 200),
      maxPages: clampInt(raw.maxPages, DEFAULT_TICKER_METRIC_CONFIG.maxPages, 1, 50),
      refreshMs: clampInt(raw.refreshMs, DEFAULT_TICKER_METRIC_CONFIG.refreshMs, 30 * 1000, 60 * 60 * 1000),
      sort: raw.sort || DEFAULT_TICKER_METRIC_CONFIG.sort,
      futureSkewMs: Math.max(0, Number(raw.futureSkewMs) || 0),
      trendMinPct: Math.max(0, Number(raw.trendMinPct) || 0),
      metricKeys,
      windowLabel: formatLookback(lookbackHours),
    };
  }

  function itemListedAtMs(item, nowMs = Date.now()) {
    const raw = item && (item.listing_time_utc || item.listingTimeUtc);
    const parsed = raw ? Date.parse(raw) : NaN;
    if (Number.isFinite(parsed)) return parsed;
    const ageMin = finiteNum(item && item.ageMin);
    return ageMin == null ? null : nowMs - ageMin * 60 * 1000;
  }
  function itemProcessedAtMs(item) {
    const raw = item && (item.processed_at || item.processedAt);
    const parsed = raw ? Date.parse(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  }
  function itemRatio(item) {
    return finiteNum(item && ((item.pricing && item.pricing.price_to_melt_ratio) ?? item.ratio));
  }
  function itemAllIn(item) {
    return finiteNum(item && (item.all_in_price_usd ?? item.allIn ?? item.price));
  }
  function itemMeltValue(item) {
    return finiteNum(item && ((item.pricing && item.pricing.estimated_gold_melt_value_usd) ?? item.meltValue));
  }
  function itemValueScore(item) {
    const explicit = finiteNum(item && item.valueScore);
    if (explicit != null) return explicit;
    return valueScoreForRatio(itemRatio(item));
  }
  function itemValueTier(item) {
    if (item && item.valueTier) return String(item.valueTier);
    return valueTierForRatio(itemRatio(item));
  }
  function itemConfidence(item) {
    const raw = item && (item.overall_confidence ?? item.overallConfidence);
    if (raw == null || raw === "") return null;
    const label = String(raw).trim().toLowerCase();
    if (CONFIDENCE_SCORES[label] != null) return CONFIDENCE_SCORES[label];
    const n = Number(label);
    if (!Number.isFinite(n)) return null;
    if (n > 1 && n <= 100) return n / 100;
    return n >= 0 && n <= 1 ? n : null;
  }
  function itemBestOffer(item) {
    return (item && (item.best_offer_enabled === true || item.bestOffer === true)) || false;
  }
  function itemTypeCode(item) {
    return item && (item.item_type || item.itemType || item.jewelry_type_guess || item.jewelryTypeGuess) || null;
  }
  function itemKarat(item) {
    const k = finiteNum(item && item.karat);
    return k == null ? null : Math.round(k);
  }
  function tickerThreshold(code) {
    const fromRegistry = dealTierThreshold(code);
    return Number.isFinite(fromRegistry) ? fromRegistry : TICKER_THRESHOLD_FALLBACKS[code];
  }
  function sortedNumbers(values) {
    return values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  }
  function median(values) {
    const arr = sortedNumbers(values);
    if (!arr.length) return null;
    const mid = Math.floor(arr.length / 2);
    return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
  }
  function average(values) {
    const arr = values.filter((v) => Number.isFinite(v));
    return arr.length ? arr.reduce((sum, v) => sum + v, 0) / arr.length : null;
  }
  function mode(values) {
    const counts = new Map();
    values.filter((v) => v != null && v !== "").forEach((v) => {
      const key = String(v);
      const entry = counts.get(key) || { value: v, count: 0 };
      entry.count += 1;
      counts.set(key, entry);
    });
    return Array.from(counts.values()).sort((a, b) =>
      b.count - a.count || String(a.value).localeCompare(String(b.value))
    )[0] || null;
  }
  function humanizeItemType(code) {
    if (!code) return null;
    if (ITEM_TYPE_TICKER_LABELS[code]) return ITEM_TYPE_TICKER_LABELS[code];
    const words = String(code).replace(/_/g, " ").split(" ").filter(Boolean);
    const label = words.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
    if (/s$/i.test(label)) return label;
    return `${label}s`;
  }
  function formatMoney(value, digits = 0) {
    if (!Number.isFinite(value)) return null;
    return "$" + value.toLocaleString(undefined, {
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    });
  }
  function formatRatio(value) {
    return Number.isFinite(value) ? `${value.toFixed(2)}x melt` : null;
  }
  function formatDuration(ms) {
    if (!Number.isFinite(ms) || ms < 0) return null;
    if (ms < 60 * 1000) return `${Math.round(ms / 1000)}s`;
    if (ms < 90 * 60 * 1000) return `${Math.round(ms / (60 * 1000))}m`;
    const hours = ms / (60 * 60 * 1000);
    if (hours < 24) return `${hours < 10 ? hours.toFixed(1) : Math.round(hours)}h`;
    return `${Math.round(hours / 24)}d`;
  }
  function bestRatio(items) {
    const ratios = sortedNumbers(items.map(itemRatio));
    return ratios.length ? ratios[0] : null;
  }
  function metric(label, value, detail, tone = "neutral", extra = {}) {
    if (value == null || value === "") return null;
    return { label, value, detail: detail || "", tone, ...extra };
  }

  function trendFor(currentValue, previousValue, config) {
    if (!Number.isFinite(currentValue) || !Number.isFinite(previousValue) || previousValue === 0) {
      return null;
    }
    const delta = currentValue - previousValue;
    const pct = delta / Math.abs(previousValue) * 100;
    if (Math.abs(pct) < (config.trendMinPct || 0)) {
      return { direction: "flat", label: "0.00%", delta, pct };
    }
    const direction = delta > 0 ? "up" : "down";
    return {
      direction,
      label: `${delta > 0 ? "+" : ""}${pct.toFixed(2)}%`,
      delta,
      pct,
    };
  }

  function buildTickerWindowStats(items, { startMs, endMs, effectiveNowMs, thresholds }) {
    const windowItems = (items || []).filter((item) => {
      const listedAt = itemListedAtMs(item, effectiveNowMs);
      return listedAt != null && listedAt >= startMs && listedAt < endMs;
    });
    const pricedItems = windowItems.filter((item) => itemRatio(item) != null);
    const dealItems = pricedItems.filter((item) => itemRatio(item) <= thresholds.dealMax);
    return {
      startMs,
      endMs,
      effectiveNowMs,
      windowItems,
      pricedItems,
      dealItems,
      underMeltItems: pricedItems.filter((item) => itemRatio(item) <= thresholds.underMax),
      greatDealBandItems: pricedItems.filter((item) => {
        const ratio = itemRatio(item);
        return ratio > thresholds.underMax && ratio <= thresholds.greatMax;
      }),
      acceptsOfferItems: pricedItems.filter((item) =>
        itemBestOffer(item) && itemRatio(item) <= thresholds.offerMax
      ),
    };
  }

  function buildTickerMetricContext({ items = [], spot = null, config, nowMs, meta } = {}) {
    const cfg = resolveTickerMetricConfig(config);
    const now = Number.isFinite(nowMs) ? nowMs : Date.now();
    const cutoffMs = now - cfg.lookbackHours * 60 * 60 * 1000;
    const previousCutoffMs = now - cfg.lookbackHours * 2 * 60 * 60 * 1000;
    const thresholds = {
      underMax: tickerThreshold("deep_discount"),
      greatMax: tickerThreshold("steal"),
      dealMax: tickerThreshold("deal"),
      offerMax: tickerThreshold("negotiable"),
    };
    const current = buildTickerWindowStats(items, {
      startMs: cutoffMs,
      endMs: now + cfg.futureSkewMs,
      effectiveNowMs: now,
      thresholds,
    });
    const previous = buildTickerWindowStats(items, {
      startMs: previousCutoffMs,
      endMs: cutoffMs,
      effectiveNowMs: cutoffMs,
      thresholds,
    });
    return {
      config: cfg,
      nowMs: now,
      cutoffMs,
      previousCutoffMs,
      meta: meta || {},
      spot,
      suffix: `(${cfg.windowLabel})`,
      current,
      previous,
      windowItems: current.windowItems,
      pricedItems: current.pricedItems,
      dealItems: current.dealItems,
      underMeltItems: current.underMeltItems,
      greatDealBandItems: current.greatDealBandItems,
      acceptsOfferItems: current.acceptsOfferItems,
    };
  }

  function previousLiveSpotPrice(spot) {
    const previous =
      spot && (
        spot.previousLive ||
        spot.previous_live ||
        (spot.metals && spot.metals.gold && (spot.metals.gold.previousLive || spot.metals.gold.previous_live))
      );
    return previous ? finiteNum(previous.pricePerGram ?? previous.price_per_gram_usd) : null;
  }

  const TICKER_METRIC_DEFINITIONS = [
    {
      key: "gold_price",
      build(ctx) {
        const perG = finiteNum(ctx.spot && (ctx.spot.pricePerGram ?? ctx.spot.price_per_gram_usd));
        if (perG == null) return null;
        const perOz = finiteNum(ctx.spot && (ctx.spot.pricePerOz ?? ctx.spot.price_per_oz_usd)) || perG * 31.1035;
        const source = ctx.spot && (ctx.spot.source || "live");
        return metric(
          "Gold Price",
          `${formatMoney(perG, 2)}/g`,
          `${formatMoney(perOz, 0)}/oz - ${source}`,
          "live",
          { trend: trendFor(perG, previousLiveSpotPrice(ctx.spot), ctx.config) },
        );
      },
    },
    {
      key: "best_under_melt",
      build(ctx) {
        const value = bestRatio(ctx.underMeltItems);
        return metric(
          `Best Under Melt ${ctx.suffix}`,
          formatRatio(value),
          "lowest ratio",
          "live",
          { trend: trendFor(value, bestRatio(ctx.previous.underMeltItems), ctx.config) },
        );
      },
    },
    {
      key: "best_great_deal",
      build(ctx) {
        const value = bestRatio(ctx.greatDealBandItems);
        return metric(
          `Best Great Deal ${ctx.suffix}`,
          formatRatio(value),
          "band best",
          "live",
          { trend: trendFor(value, bestRatio(ctx.previous.greatDealBandItems), ctx.config) },
        );
      },
    },
    {
      key: "best_accepts_offers",
      build(ctx) {
        const value = bestRatio(ctx.acceptsOfferItems);
        return metric(
          `Best Accepts Offers ${ctx.suffix}`,
          formatRatio(value),
          "seller accepts offers",
          "live",
          { trend: trendFor(value, bestRatio(ctx.previous.acceptsOfferItems), ctx.config) },
        );
      },
    },
    {
      key: "median_ratio",
      build(ctx) {
        const value = median(ctx.pricedItems.map(itemRatio));
        return metric(
          `Median Ratio ${ctx.suffix}`,
          formatRatio(value),
          `${ctx.pricedItems.length} priced`,
          "neutral",
          { trend: trendFor(value, median(ctx.previous.pricedItems.map(itemRatio)), ctx.config) },
        );
      },
    },
    {
      key: "median_all_in_price",
      build(ctx) {
        const value = median(ctx.windowItems.map(itemAllIn));
        return metric(
          `Median All-In ${ctx.suffix}`,
          formatMoney(value, 0),
          `${ctx.windowItems.length} listings`,
          "neutral",
          { trend: trendFor(value, median(ctx.previous.windowItems.map(itemAllIn)), ctx.config) },
        );
      },
    },
    {
      key: "top_karat_deals",
      build(ctx) {
        const top = mode(ctx.dealItems.map(itemKarat));
        return metric(`Top Karat In Deals ${ctx.suffix}`, top ? `${top.value}K` : null, top ? `${top.count} deals` : "", "neutral");
      },
    },
    {
      key: "top_item_type",
      build(ctx) {
        const top = mode(ctx.windowItems.map(itemTypeCode));
        return metric(`Top Item Type ${ctx.suffix}`, top ? humanizeItemType(top.value) : null, top ? `${top.count} listings` : "", "neutral");
      },
    },
    {
      key: "avg_process_lag",
      build(ctx) {
        const lags = ctx.windowItems.map((item) => {
          const listedAt = itemListedAtMs(item, ctx.nowMs);
          const processedAt = itemProcessedAtMs(item);
          return listedAt != null && processedAt != null && processedAt >= listedAt
            ? processedAt - listedAt
            : null;
        }).filter((v) => v != null);
        const previousLags = ctx.previous.windowItems.map((item) => {
          const listedAt = itemListedAtMs(item, ctx.previous.effectiveNowMs);
          const processedAt = itemProcessedAtMs(item);
          return listedAt != null && processedAt != null && processedAt >= listedAt
            ? processedAt - listedAt
            : null;
        }).filter((v) => v != null);
        const value = average(lags);
        return metric(
          `Avg Process Lag ${ctx.suffix}`,
          formatDuration(value),
          `${lags.length} processed`,
          "neutral",
          { trend: trendFor(value, average(previousLags), ctx.config) },
        );
      },
    },
    {
      key: "avg_confidence",
      build(ctx) {
        const scores = ctx.windowItems.map(itemConfidence).filter((v) => v != null);
        const avg = average(scores);
        const previousScores = ctx.previous.windowItems.map(itemConfidence).filter((v) => v != null);
        return metric(
          `Avg Confidence ${ctx.suffix}`,
          avg == null ? null : `${Math.round(avg * 100)}%`,
          `${scores.length} scored`,
          "neutral",
          { trend: trendFor(avg, average(previousScores), ctx.config) },
        );
      },
    },
    {
      key: "largest_melt_spread",
      build(ctx) {
        const spreads = ctx.windowItems.map((item) => {
          const melt = itemMeltValue(item);
          const allIn = itemAllIn(item);
          return melt != null && allIn != null ? melt - allIn : null;
        }).filter((v) => Number.isFinite(v) && v > 0);
        const spread = spreads.length ? Math.max(...spreads) : null;
        const previousSpreads = ctx.previous.windowItems.map((item) => {
          const melt = itemMeltValue(item);
          const allIn = itemAllIn(item);
          return melt != null && allIn != null ? melt - allIn : null;
        }).filter((v) => Number.isFinite(v) && v > 0);
        const previousSpread = previousSpreads.length ? Math.max(...previousSpreads) : null;
        return metric(
          `Largest Melt Spread ${ctx.suffix}`,
          spread == null ? null : `${formatMoney(spread, 0)} under`,
          "melt minus all-in",
          "live",
          { trend: trendFor(spread, previousSpread, ctx.config) },
        );
      },
    },
    {
      key: "top_value_tier",
      build(ctx) {
        const scored = ctx.windowItems
          .map((item) => ({ score: itemValueScore(item), tier: itemValueTier(item) }))
          .filter((entry) => entry.score != null && entry.tier)
          .sort((a, b) => b.score - a.score)[0];
        const previousScore = ctx.previous.windowItems.map(itemValueScore).filter((v) => v != null).sort((a, b) => b - a)[0];
        return metric(
          `Top Value Tier ${ctx.suffix}`,
          scored ? `${scored.tier} tier` : null,
          scored ? "best price-to-melt tier" : "",
          "live",
          { trend: trendFor(scored && scored.score, previousScore, ctx.config) },
        );
      },
    },
    {
      key: "newest_deal",
      build(ctx) {
        const newest = ctx.dealItems
          .map((item) => itemListedAtMs(item, ctx.nowMs))
          .filter((v) => v != null)
          .sort((a, b) => b - a)[0];
        return metric(`Newest Deal ${ctx.suffix}`, newest == null ? null : `${formatDuration(ctx.nowMs - newest)} ago`, "listed", "live");
      },
    },
  ];

  function applyTickerMetricSampleMeta(key, row, ctx) {
    if (key === "gold_price") {
      return { ...row, sample: { scope: "spot" } };
    }
    const currentWindowCapped = Boolean(ctx.meta.currentWindowCapped);
    const previousWindowCapped = Boolean(ctx.meta.previousWindowCapped);
    const trendSampleCapped = Boolean(row.trend && (ctx.meta.trendSampleCapped || currentWindowCapped || previousWindowCapped));
    const sample = {
      scope: "item_window",
      pageBudgetExhausted: Boolean(ctx.meta.pageBudgetExhausted),
      currentWindowCapped,
      previousWindowCapped,
      valueSampleCapped: currentWindowCapped,
      trendSampleCapped,
    };
    return {
      ...row,
      trend: trendSampleCapped ? null : row.trend,
      trendSuppressed: trendSampleCapped,
      sample,
    };
  }

  function buildTickerMetrics(opts = {}) {
    const ctx = buildTickerMetricContext(opts);
    const defs = new Map(TICKER_METRIC_DEFINITIONS.map((def) => [def.key, def]));
    const metrics = ctx.config.metricKeys
      .map((key) => defs.get(key))
      .filter(Boolean)
      .map((def) => {
        const row = def.build(ctx);
        if (!row) return null;
        return { key: def.key, ...applyTickerMetricSampleMeta(def.key, row, ctx) };
      })
      .filter(Boolean);
    const hasItemMetric = metrics.some((row) => row.key !== "gold_price");
    if (!hasItemMetric) {
      metrics.push({
        key: "item_window_pending",
        label: `Item Metrics (${ctx.config.windowLabel})`,
        value: ctx.windowItems.length ? "Insufficient data" : "Collecting",
        detail: ctx.windowItems.length ? "insufficient metric data" : "waiting for listings",
        tone: "neutral",
        sample: {
          scope: "item_window",
          pageBudgetExhausted: Boolean(ctx.meta.pageBudgetExhausted),
          currentWindowCapped: Boolean(ctx.meta.currentWindowCapped),
          previousWindowCapped: Boolean(ctx.meta.previousWindowCapped),
          valueSampleCapped: Boolean(ctx.meta.currentWindowCapped),
          trendSampleCapped: false,
        },
      });
    }
    return metrics;
  }

  async function getTickerMetricWindow(opts = {}) {
    const config = resolveTickerMetricConfig(opts.config || {});
    const nowMs = Number.isFinite(opts.nowMs) ? opts.nowMs : Date.now();
    const cutoffMs = nowMs - config.lookbackHours * 60 * 60 * 1000;
    const previousCutoffMs = nowMs - config.lookbackHours * 2 * 60 * 60 * 1000;
    const items = [];
    let page = opts.page || null;
    let pagesFetched = 0;
    let stoppedAtOlderCurrentWindow = false;
    let stoppedAtOlderPreviousWindow = false;

    while (pagesFetched < config.maxPages) {
      const resp = await apiFetch("/items", {
        signal: opts.signal,
        query: {
          sort: config.sort,
          limit: config.pageLimit,
          page,
        },
      });
      const pageRows = rows(resp);
      if (!pageRows.length) break;
      pagesFetched += 1;

      for (const item of pageRows) {
        const listedAt = itemListedAtMs(item, nowMs);
        if (listedAt == null || listedAt > nowMs + config.futureSkewMs) continue;
        if (listedAt < cutoffMs) stoppedAtOlderCurrentWindow = true;
        if (listedAt >= previousCutoffMs) items.push(item);
        else stoppedAtOlderPreviousWindow = true;
      }

      page = (resp && resp.next_page) || null;
      if (!page || stoppedAtOlderPreviousWindow) break;
    }

    const pageBudgetExhausted = Boolean(page && pagesFetched >= config.maxPages);
    const currentWindowCapped = pageBudgetExhausted && !stoppedAtOlderCurrentWindow;
    const previousWindowCapped = pageBudgetExhausted && !stoppedAtOlderPreviousWindow;

    return {
      items,
      meta: {
        lookbackHours: config.lookbackHours,
        windowLabel: config.windowLabel,
        cutoffAt: new Date(cutoffMs).toISOString(),
        previousCutoffAt: new Date(previousCutoffMs).toISOString(),
        generatedAt: new Date(nowMs).toISOString(),
        pagesFetched,
        capped: currentWindowCapped || previousWindowCapped,
        pageBudgetExhausted,
        currentWindowCapped,
        previousWindowCapped,
        trendSampleCapped: currentWindowCapped || previousWindowCapped,
        stoppedAtOlderItem: stoppedAtOlderPreviousWindow,
        stoppedAtOlderCurrentWindow,
        stoppedAtOlderPreviousWindow,
        nextPage: page,
      },
    };
  }

  // ---- the client: one method per endpoint in the brief --------------------
  const KS = {
    base: API_BASE,
    ApiError,

    // -- auth (magic-link only) --
    requestMagicLink: (email, opts = {}) =>
      apiFetch("/auth/magic-link/request", { ...opts, method: "POST", body: { email } }),
    consumeMagicLink: (token, opts = {}) =>
      apiFetch("/auth/magic-link/consume", { ...opts, method: "POST", body: { token } }),
    logout: (opts = {}) => apiFetch("/auth/logout", {
      ...opts,
      method: "POST",
      expectedStatuses: [204],
    }),

    // -- public browse / deals --
    // feed: todays_deals | deep_discount | negotiable | deal | steal | near | unscored_gold | all
    async getFeed(feed, opts = {}) {
      const {
        nearTier = "steal",
        includeReview = true,
        filters = {},
        sort = "newest",
        limit = 50,
        page,
      } = opts;
      let resp;
      const filterQuery = browseFilterQuery(filters);
      const dealQuery = {
        limit,
        page,
        ...filterQuery,
        ...reviewRequiredDealQuery(includeReview),
      };
      if (feed === "all") {
        resp = await apiFetch("/items", {
          query: {
            sort,
            limit,
            page,
            ...filterQuery,
            ...reviewRequiredItemQuery(includeReview),
          },
        });
      } else if (feed === "todays_deals") {
        resp = await apiFetch("/deals/todays-deals", {
          query: dealQuery,
        });
      } else if (feed === "unscored_gold" || feed === "needs") {
        resp = await apiFetch("/deals/unscored-gold", {
          query: dealQuery,
        });
      } else if (feed === "negotiable" || feed === "offer") {
        resp = await apiFetch(feed === "offer" ? "/deals/offer" : "/deals/negotiable", {
          query: dealQuery,
        });
      } else if (feed === "near") {
        const apiNearTier = canonicalNearMissTierCode(nearTier);
        resp = await apiFetch("/deals/near-miss", {
          query: { tier: apiNearTier, ...dealQuery },
        });
      } else if (feed === "deep_discount") {
        resp = await apiFetch("/deals/deep-discount", {
          query: dealQuery,
        });
      } else {
        // deal | steal
        resp = await apiFetch("/deals/" + feed, {
          query: dealQuery,
        });
      }
      const list = rows(resp).map((row) => normalizeItem(row, {
        nearTier: feed === "near" ? nearTier : "steal",
      }));
      return { items: list, nextPage: (resp && resp.next_page) || null };
    },

    getBrowseCounts: (opts = {}) =>
      apiFetch("/browse/counts", {
        query: {
          ...browseFilterQuery(opts.filters || {}),
          ...reviewRequiredDealQuery(opts.includeReview !== false),
        },
      }).then(normalizeBrowseCounts),
    getBrowseActivity: () =>
      apiFetch("/browse/activity").then(normalizeBrowseActivity),

    getItem: (pk) => apiFetch("/items/" + encodeURIComponent(pk)).then(normalizeDetail),
    getRelated: (pk) =>
      apiFetch("/items/" + encodeURIComponent(pk) + "/related").then((r) => rows(r).map(normalizeItem)),

    search: (q, opts = {}) =>
      apiFetch("/search/items", {
        query: {
          q,
          limit: opts.limit || 50,
          page: opts.page,
          ...browseFilterQuery(opts.filters || {}),
          ...reviewRequiredItemQuery(opts.includeReview !== false),
        },
      }).then(normalizeSearch),

    getSpot: () => apiFetch("/gold/spot").then(normalizeSpot),
    getTickerMetricWindow,
    getTickerMetrics: (opts = {}) =>
      getTickerMetricWindow(opts).then((snapshot) => ({
        ...snapshot,
        metrics: buildTickerMetrics({
          items: snapshot.items,
          meta: snapshot.meta,
          spot: opts.spot,
          config: opts.config,
          nowMs: opts.nowMs,
        }),
      })),
    getDealTiers: () =>
      apiFetch("/reference/deal-tiers").then((entries) =>
        setDealTierEntries(entries)
      ),
    getReference: (name) => apiFetch("/reference/" + name),

    // -- profile --
    getProfile: (opts = {}) => apiFetch("/users/me", opts),
    updateProfile: (display_name, opts = {}) => apiFetch("/users/me", {
      ...opts,
      method: "PATCH",
      body: { display_name },
      expectedStatuses: [200],
    }),
    deleteAccount: (opts = {}) => apiFetch("/users/me", {
      ...opts,
      method: "DELETE",
      body: undefined,
      expectedStatuses: [204],
    }),

    // -- saved items --
    getSavedItems: () => apiFetch("/me/saved-items").then((r) => rows(r).map(normalizeItem)),
    saveItem: (item_pk, note) => apiFetch("/me/saved-items", { method: "POST", body: { item_pk, note } }),
    updateSavedNote: (item_pk, note) =>
      apiFetch("/me/saved-items/" + encodeURIComponent(item_pk), { method: "PATCH", body: { note } }),
    unsaveItem: (item_pk) =>
      apiFetch("/me/saved-items/" + encodeURIComponent(item_pk), { method: "DELETE" }),

    // -- saved searches --
    getSavedSearches: () => apiFetch("/me/saved-searches").then(rows),
    createSavedSearch: (name, criteria) =>
      apiFetch("/me/saved-searches", { method: "POST", body: { name, criteria } }),
    deleteSavedSearch: (id) => apiFetch("/me/saved-searches/" + id, { method: "DELETE" }),
    replaySavedSearch: (id, opts = {}) =>
      apiFetch("/me/saved-searches/" + id + "/items", { query: opts }).then((r) => rows(r).map(normalizeItem)),

    // -- alert rules --
    getAlertRules: () => apiFetch("/me/alert-rules").then((r) => rows(r).map(normalizeAlertRule)),
    createAlertRule: (body) => apiFetch("/me/alert-rules", { method: "POST", body }),
    getAlertRule: (id) => apiFetch("/me/alert-rules/" + id).then(normalizeAlertRule),
    updateAlertRule: (id, body) => apiFetch("/me/alert-rules/" + id, { method: "PATCH", body }),
    deleteAlertRule: (id) => apiFetch("/me/alert-rules/" + id, { method: "DELETE" }),
    testAlertRule: (id) => apiFetch("/me/alert-rules/" + id + "/test", { method: "POST" }),
    rescanAlertRule: (id) => apiFetch("/me/alert-rules/" + id + "/rescan", { method: "POST" }),

    // -- notification channels (Discord webhook only, per brief) --
    getChannels: () => apiFetch("/me/notification-channels").then(rows),
    createChannel: (label, secret) =>
      apiFetch("/me/notification-channels", {
        method: "POST",
        body: { channel_type: "discord_webhook", label, secret },
      }),
    updateChannel: (id, body) => apiFetch("/me/notification-channels/" + id, { method: "PATCH", body }),
    deleteChannel: (id) => apiFetch("/me/notification-channels/" + id, { method: "DELETE" }),
    testChannel: (id) => apiFetch("/me/notification-channels/" + id + "/test", { method: "POST" }),

    // -- alert history --
    getAlertHistory: () => apiFetch("/me/alerts").then(rows),
    getAlert: (id) => apiFetch("/me/alerts/" + id),

    // -- feedback --
    getFeedback: () => apiFetch("/me/feedback").then(rows),
    submitFeedback: (body) => apiFetch("/me/feedback", { method: "POST", body }),

    // -- live SSE stream of newly analyzed items --
    // returns an unsubscribe fn. onItem receives a RAW PublicItemSummary;
    // normalize it with normalizeItem before adding to state.
    streamItems({ filter, onItem, onError } = {}) {
      if (typeof EventSource === "undefined") return () => {};
      const f = filter ? "?filter=" + encodeURIComponent(typeof filter === "string" ? filter : JSON.stringify(filter)) : "";
      const es = new EventSource(API_BASE + "/events/items" + f, { withCredentials: true });
      const handle = (ev) => {
        if (!ev || !ev.data) return;
        try { onItem && onItem(JSON.parse(ev.data)); } catch (_) {}
      };
      es.onmessage = handle;
      es.addEventListener("item", handle);
      es.onerror = (e) => onError && onError(e);
      return () => es.close();
    },

    // -- anonymous public browse SSE stream --
    // returns an unsubscribe fn. onItem receives a RAW PublicItemSummary;
    // normalize it with normalizeItem before merging into browse state.
    streamPublicItems({ onItem, onError, onOpen } = {}) {
      if (typeof EventSource === "undefined") return () => {};
      const es = new EventSource(API_BASE + "/events/public-items");
      const handle = (ev) => {
        if (!ev || !ev.data) return;
        try { onItem && onItem(JSON.parse(ev.data)); } catch (_) {}
      };
      es.onmessage = handle;
      es.addEventListener("item", handle);
      es.onopen = (e) => onOpen && onOpen(e);
      es.onerror = (e) => onError && onError(e);
      return () => es.close();
    },
  };

  export {
    API_BASE,
    ApiError,
    BROWSE_COUNT_KEYS,
    DEFAULT_TICKER_METRIC_CONFIG,
    KS,
    KS as KaratScanAPI,
    buildTickerMetrics,
    browseFilterQuery,
    confidenceBand,
    getTickerMetricWindow,
    normalizeAlertRule,
    normalizeBrowseActivity,
    normalizeBrowseCounts,
    normalizeDetail,
    normalizeItem,
    normalizeSearch,
    normalizeSpot,
    resolveTickerMetricConfig,
    setAccountUnauthorizedHandler,
    valueCriterionForMinDealScore,
    valueScoreForRatio,
    valueTierForRatio,
  };
